import React, { useEffect, useState } from 'react';
import { gdprService } from '../../services/gdprService';

type GdprType = 'EXPORT' | 'DELETE';
type GdprStatus = 'PENDING' | 'PROCESSING' | 'READY' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

interface GdprRequest {
  id: string;
  userId: string;
  type: GdprType;
  status: GdprStatus;
  requestedAt: string;
  processedAt?: string;
  scheduledDeletionAt?: string;
  fileName?: string;
}

const AdminGDPRRequestsPage: React.FC = () => {
  const [requests, setRequests] = useState<GdprRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [error, setError] = useState<string>('');

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await gdprService.adminListRequests({ type: type || undefined, status: status || undefined });
      setRequests(data as any);
    } catch (e: any) {
      setError(e?.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleApprove = async (id: string) => {
    await gdprService.adminApproveDeletion(id);
    await load();
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold text-gray-900">GDPR Requests</h1>
        <button
          onClick={load}
          className="px-3 py-2 rounded bg-gray-100 hover:bg-gray-200 text-gray-800"
          disabled={loading}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="flex gap-3 mb-4">
        <select value={type} onChange={(e) => setType(e.target.value)} className="border rounded px-2 py-1">
          <option value="">All Types</option>
          <option value="EXPORT">EXPORT</option>
          <option value="DELETE">DELETE</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded px-2 py-1">
          <option value="">All Statuses</option>
          {['PENDING','PROCESSING','READY','COMPLETED','FAILED','CANCELLED'].map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <button
          onClick={load}
          className="px-3 py-2 rounded bg-orange-600 text-white hover:bg-orange-700"
        >
          Filter
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded mb-4">{error}</div>
      )}

      <div className="overflow-x-auto bg-white shadow rounded">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requested</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scheduled</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {requests.map(r => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm text-gray-900">{r.id.slice(0,8)}…</td>
                <td className="px-4 py-2 text-sm text-gray-700">{r.userId.slice(0,8)}…</td>
                <td className="px-4 py-2 text-sm font-medium">{r.type}</td>
                <td className="px-4 py-2 text-sm">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">{r.status}</span>
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">{new Date(r.requestedAt).toLocaleString()}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{r.scheduledDeletionAt ? new Date(r.scheduledDeletionAt).toLocaleDateString() : '-'}</td>
                <td className="px-4 py-2 text-sm flex gap-2">
                  {/* Admins can't download user exports via owner-only endpoint; hide download in admin view */}
                  {r.type === 'DELETE' && r.status === 'PENDING' && (
                    <button onClick={() => handleApprove(r.id)} className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700">Approve</button>
                  )}
                </td>
              </tr>
            ))}
            {requests.length === 0 && !loading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-500">No requests</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminGDPRRequestsPage;
